import React from 'react';
import { Alert, Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { StatementTable } from '../../services/financialStatement.service';
import { formatMoney } from '../../utils/format';

const cellText = (v: StatementTable['rows'][number]['cells'][number]): string => {
    if (v === null || v === undefined) return '';
    if (typeof v === 'number') return formatMoney(v);
    if (typeof v === 'object') return v.value === null ? 'n/a' : `${v.value.toFixed(2)}%`;
    return v;
};

/** Generic renderer for the server-built statement tables (same figures as the PDF/XLSX). */
const StatementTableView: React.FC<{ table: StatementTable }> = ({ table }) => (
    <Box>
        {table.meta.map(m => <Typography key={m} variant="body2" color="text.secondary">{m}</Typography>)}
        <TableContainer data-help-section="accounting.statementTable.table" component={Paper} variant="outlined" sx={{ mt: 1.5 }}>
            <Table size="small" sx={{ minWidth: 720 }}>
                <TableHead>
                    <TableRow>
                        {table.columns.map((c, i) => (
                            <TableCell key={i} align={c.numeric ? 'right' : 'left'} sx={{ fontWeight: 700 }}>{c.header}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {table.rows.map((r, ri) => {
                        const strong = r.kind !== 'DATA';
                        return (
                            <TableRow key={ri} hover sx={{ borderTop: r.kind === 'TOTAL' || r.kind === 'GRAND' ? '2px solid' : undefined, bgcolor: r.kind === 'SECTION' ? 'action.hover' : undefined }}>
                                {table.columns.map((c, ci) => (
                                    <TableCell
                                        key={ci}
                                        align={c.numeric ? 'right' : 'left'}
                                        sx={{ fontWeight: strong ? 700 : 400, whiteSpace: c.numeric ? 'nowrap' : undefined, pl: ci === 0 ? 2 + r.indent * 2 : undefined }}
                                    >
                                        {cellText(r.cells[ci])}
                                    </TableCell>
                                ))}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
        {table.notes.map(n => <Alert key={n} severity="info" sx={{ mt: 1 }}>{n}</Alert>)}
    </Box>
);

export default StatementTableView;
